import {
  Flex,
  HStack,
  Icon,
  Image,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { pluralise, toColor } from "modules/utils";
import { FiBox, FiCheckCircle, FiClock } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  MFActiveRequest,
  MFCatalogItem,
  MFSelectableResourceAccess,
} from "state/members/access_v2/catalogs";
import { catalogItemTenants } from "state/members/access_v2/catalogNames";
import { RequestAccessModal } from "components/RequestAccess";
import "./styles.css";

const CatalogItem = ({
  item,
  activeRequests,
  currentAccess,
  isOpen: defaultOpen,
  catalogId,
}: {
  item: MFCatalogItem;
  activeRequests: MFActiveRequest[];
  currentAccess: MFSelectableResourceAccess[];
  isOpen: boolean;
  catalogId: string;
}) => {
  const {
    isOpen,
    onOpen: doOpen,
    onClose: doClose,
  } = useDisclosure({
    defaultIsOpen: defaultOpen,
  });
  const nav = useNavigate();
  const tenants = catalogItemTenants(item.catalogItemSelectableResources);

  const onOpen = () => {
    nav(`/members/access/catalogs/${catalogId}?item_id=${item.id}`);
    doOpen();
  };

  const onClose = () => {
    nav(`/members/access/catalogs/${catalogId}`, { replace: true });
    doClose();
  };

  return (
    <>
      <Flex
        justify="center"
        align="center"
        border="1px solid"
        borderColor={"gray.200"}
        borderRadius={"10px"}
        w="220px"
        h="210px"
        key={item.id}
        p={2}
        _hover={{
          shadow: "md",
          transform: "translateY(-1px) scale(1.05)",
          cursor: "pointer",
        }}
        onClick={onOpen}
        position="relative"
      >
        <VStack spacing={4}>
          {item.imageUrl ? (
            <Image
              src={item.imageUrl}
              boxSize="48px"
              borderRadius="5px"
              fallback={
                <Flex
                  boxSize={"48px"}
                  bg={toColor(item.name) + ".500"}
                  rounded="5px"
                  justify="center"
                  align="center"
                >
                  <Icon
                    as={FiBox}
                    boxSize={"32px"}
                    rounded={"5px"}
                    color="white"
                  />
                </Flex>
              }
            />
          ) : (
            <Flex
              boxSize={"48px"}
              bg={toColor(item.name) + ".500"}
              rounded="5px"
              justify="center"
              align="center"
            >
              <Icon as={FiBox} boxSize={"32px"} rounded={"5px"} color="white" />
            </Flex>
          )}
          <VStack spacing={0} w="full">
            <Text fontSize="16px" fontWeight="500" textAlign={"center"}>
              {item.name}
            </Text>
            {tenants && (
              <Text
                textAlign={"center"}
                fontSize="14px"
                fontWeight="500"
                color="gray.700"
                noOfLines={1}
              >
                {tenants}
              </Text>
            )}
            <Text
              textAlign={"center"}
              fontSize="14px"
              fontWeight="400"
              color="gray.600"
            >
              {item.catalogItemSelectableResources.length}{" "}
              {pluralise(
                "resource",
                item.catalogItemSelectableResources.length,
              )}
            </Text>
          </VStack>
          <HStack spacing={2} justify="center" w="full">
            {currentAccess.some((ca) => ca.status === "active") && (
              <Tooltip label="You already have access to one or more resources">
                <Tag size="sm" colorScheme="green">
                  <TagLeftIcon as={FiCheckCircle} />
                  <TagLabel>You have access</TagLabel>
                </Tag>
              </Tooltip>
            )}
            {activeRequests.length > 0 && (
              <Tag size="sm" colorScheme="orange">
                <TagLeftIcon as={FiClock} />
                <TagLabel>Active Request</TagLabel>
              </Tag>
            )}
          </HStack>
        </VStack>
      </Flex>
      {isOpen && (
        <RequestAccessModal
          isOpen={isOpen}
          onClose={onClose}
          initialCatalogId={catalogId}
          initialCatalogItemId={item.id}
          // If this catalog item exposes exactly one selectable resource,
          // pre-add it to the basket — the click was unambiguous so the
          // user shouldn't need a second click. With 2+ resources we leave
          // the basket empty and let them pick.
          initialBasket={
            item.catalogItemSelectableResources?.length === 1
              ? [
                  `${catalogId}__${item.id}__${item.catalogItemSelectableResources[0].selectableResource.id}`,
                ]
              : []
          }
        />
      )}
    </>
  );
};

export default CatalogItem;
